/**
 * @file     StatRow.tsx
 * @purpose  Hub stat row: two to four headline figures in a hairline matrix
 *           with micro-labels, an optional delta per tile and a named source
 *           line. A figure with trailing words ("$241 a month") sets the words as
 *           a muted unit, and a worded value ("Apartment") sets smaller. The
 *           row sizes to its own width: figures never wrap, scale with the
 *           tile, and the matrix folds to two columns in a narrow column.
 *           A null, empty or placeholder figure drops its tile, so a dash
 *           or "Unavailable" never renders. No hooks or directive, so it renders
 *           from a Server Component or inside a client shell alike.
 */
import Box from '@mui/material/Box';
import { layoutTokens } from '@/design-system/tokens/layout';
import { motionTokens } from '@/design-system/tokens/motion';
import { formatDateTime } from '@/utils/dateFormat';
import {
  hairline,
  headingFont,
  hubColors,
  isDisplayableFigure,
  microLabelSx,
  reducedMotionSx,
} from './hubKitStyles';

export type StatDeltaTone = 'up' | 'down' | 'flat';

export interface StatRowItem {
  label: string;
  /** Preformatted figure. Null, undefined, '', a dash or "Unavailable" drops the tile. */
  value: string | number | null | undefined;
  /** Short change line beside the label, e.g. "+4.2% vs last month". */
  delta?: string | null;
  /** Colours the delta. Defaults to muted. */
  deltaTone?: StatDeltaTone;
  /** One short line under the figure on what it means, e.g. "Set by the Bank of Canada." */
  note?: string | null;
}

export interface StatRowProps {
  items: StatRowItem[];
  /** Named source, e.g. "MLS® Reciprocity feed". Rendered as "Source: ...". */
  source?: string | null;
  /** Last refresh of the figures, appended to the source line. */
  updatedAt?: string | Date | null;
  /** Renders shimmer tiles in place of figures. */
  loading?: boolean;
  /** Accessible name for the figure list. */
  ariaLabel?: string;
}

const MAX_TILES = 4;

const toneColor: Record<StatDeltaTone, string> = {
  up: hubColors.statusSuccess,
  down: hubColors.statusError,
  flat: hubColors.textMuted,
};

function formatFigure(value: string | number): string {
  return typeof value === 'number' ? value.toLocaleString('en-CA') : value;
}

export interface FigureParts {
  figure: string;
  /** Words after a leading figure, e.g. "a month" in "$241 a month". */
  unit: string | null;
  /** A worded value with no leading figure, e.g. "Apartment". Set smaller so it does not break mid-word. */
  text: boolean;
}

const LEADING_FIGURE = /^([$]?[-+]?\d[\d.,]*[KMB%]?)\s+(\p{L}.*)$/u;
const WORDED = /\p{L}{3,}/u;

/** Numbers render whole; a figure with trailing words splits into figure and unit; a worded value reads as text. */
export function splitFigure(value: string | number): FigureParts {
  const formatted = formatFigure(value);
  if (typeof value === 'number') return { figure: formatted, unit: null, text: false };
  const lead = LEADING_FIGURE.exec(formatted.trim());
  if (lead) return { figure: lead[1], unit: lead[2], text: false };
  return { figure: formatted, unit: null, text: WORDED.test(formatted) };
}

export function sourceLine(source?: string | null, updatedAt?: string | Date | null): string {
  const updated = updatedAt ? formatDateTime(updatedAt) : '';
  const parts = [source ? `Source: ${source}.` : '', updated ? `Updated ${updated}.` : ''].filter(Boolean);
  return parts.join(' ');
}

const shimmer = {
  borderRadius: `${layoutTokens.radius.sm}px`,
  backgroundColor: hubColors.skeleton,
  animation: `hubStatShimmer ${motionTokens.loading.skeletonPulseDuration}ms ease-in-out infinite`,
  '@keyframes hubStatShimmer': { '0%, 100%': { opacity: 0.55 }, '50%': { opacity: 1 } },
  ...reducedMotionSx,
} as const;

/**
 * The row sizes itself to its own width, not the viewport, so four tiles in a
 * narrow hero column behave like four tiles on a phone. Below MIN_TILE_WIDTH
 * per tile the matrix folds to two columns; the figure size follows the tile
 * width so a number never breaks mid-figure.
 */
const CONTAINER = 'hubstatrow';
const MIN_TILE_WIDTH = 160;

/** Container query that switches the row to one column per tile. */
export function fullRowQuery(count: number): string {
  return `@container ${CONTAINER} (min-width: ${count * MIN_TILE_WIDTH}px)`;
}

const containerSx = { width: '100%', containerType: 'inline-size', containerName: CONTAINER } as const;

function foldedColumns(count: number): number {
  return count === 1 ? 1 : 2;
}

/** Fluid figure size: about seven tabular characters fill a tile. */
export function figureFontSize(columns: number, text: boolean): string {
  return text
    ? `clamp(15px, calc(100cqi / ${columns * 11}), 26px)`
    : `clamp(18px, calc(100cqi / ${columns * 7}), 42px)`;
}

function tileSx(index: number, count: number) {
  const folded = foldedColumns(count);
  const leftFolded = index % folded !== 0;
  const leftFull = index !== 0;
  return {
    minWidth: 0,
    display: 'flex',
    flexDirection: 'column-reverse',
    justifyContent: 'flex-end',
    gap: 1,
    py: { xs: 2, md: 3 },
    pl: leftFolded ? 2 : 0,
    pr: 2,
    borderTop: hairline,
    borderLeft: leftFolded ? hairline : 'none',
    [fullRowQuery(count)]: {
      pl: leftFull ? 3 : 0,
      pr: 3,
      borderLeft: leftFull ? hairline : 'none',
    },
  } as const;
}

function gridSx(count: number) {
  const folded = foldedColumns(count);
  return {
    m: 0,
    p: 0,
    display: 'grid',
    gridTemplateColumns: `repeat(${folded}, minmax(0, 1fr))`,
    [fullRowQuery(count)]: { gridTemplateColumns: `repeat(${count}, minmax(0, 1fr))` },
  } as const;
}

function valueSx(count: number, text: boolean) {
  return {
    m: 0,
    fontFamily: headingFont,
    fontWeight: 500,
    fontSize: figureFontSize(foldedColumns(count), text),
    lineHeight: text ? 1.2 : 1.05,
    letterSpacing: text ? '-0.012em' : '-0.02em',
    fontVariantNumeric: 'tabular-nums',
    color: hubColors.textPrimary,
    overflowWrap: 'break-word',
    textWrap: 'balance',
    [fullRowQuery(count)]: { fontSize: figureFontSize(count, text) },
  } as const;
}

export function StatRow({ items, source, updatedAt, loading = false, ariaLabel = 'Key figures' }: StatRowProps) {
  const tiles = items.filter((item) => isDisplayableFigure(item.value)).slice(0, MAX_TILES);
  const caption = sourceLine(source, updatedAt);

  if (loading) {
    const count = Math.min(Math.max(items.length, 2), MAX_TILES);
    return (
      <Box sx={containerSx}>
        <Box aria-busy="true" aria-label={ariaLabel} role="group" sx={gridSx(count)}>
          {Array.from({ length: count }, (_, i) => (
            <Box key={i} sx={{ ...tileSx(i, count), flexDirection: 'column' }}>
              <Box sx={{ ...shimmer, width: '70%', height: { xs: 26, md: 38 } }} />
              <Box sx={{ ...shimmer, width: '50%', height: 12 }} />
            </Box>
          ))}
        </Box>
      </Box>
    );
  }

  if (tiles.length === 0) return null;

  return (
    <Box sx={containerSx}>
      <Box component="dl" aria-label={ariaLabel} sx={gridSx(tiles.length)}>
        {tiles.map((item, i) => {
          const delta = isDisplayableFigure(item.delta) ? item.delta : null;
          const parts = splitFigure(item.value as string | number);
          return (
            <Box key={item.label} sx={tileSx(i, tiles.length)}>
              <Box
                component="dt"
                sx={{
                  ...microLabelSx,
                  display: 'flex',
                  flexWrap: 'wrap',
                  alignItems: 'baseline',
                  columnGap: 1,
                  rowGap: 0.25,
                }}
              >
                <span>{item.label}</span>
                {delta ? (
                  <Box
                    component="span"
                    sx={{
                      letterSpacing: 0,
                      textTransform: 'none',
                      fontWeight: 500,
                      fontSize: 12.5,
                      fontVariantNumeric: 'tabular-nums',
                      color: toneColor[item.deltaTone ?? 'flat'],
                    }}
                  >
                    {delta}
                  </Box>
                ) : null}
              </Box>
              <Box component="dd" sx={valueSx(tiles.length, parts.text)}>
                {parts.text ? (
                  parts.figure
                ) : (
                  <Box component="span" sx={{ whiteSpace: 'nowrap' }}>
                    {parts.figure}
                  </Box>
                )}
                {parts.unit ? (
                  <>
                    {' '}
                    <Box
                      component="span"
                      sx={{
                        display: 'inline-block',
                        fontFamily: 'inherit',
                        fontSize: { xs: 13, md: 15 },
                        fontWeight: 500,
                        letterSpacing: 0,
                        lineHeight: 1.3,
                        color: hubColors.textMuted,
                      }}
                    >
                      {parts.unit}
                    </Box>
                  </>
                ) : null}
              </Box>
              {item.note ? (
                // The tile is a column-reverse flex, so order -1 puts the note under the label.
                <Box component="dd" sx={{ order: -1, m: 0, fontSize: 13, lineHeight: 1.5, color: hubColors.textSecondary, maxWidth: '34ch' }}>
                  {item.note}
                </Box>
              ) : null}
            </Box>
          );
        })}
      </Box>
      {caption ? (
        <Box component="p" sx={{ m: 0, mt: 2, fontSize: 12, lineHeight: 1.5, color: hubColors.textMuted }}>
          {caption}
        </Box>
      ) : null}
    </Box>
  );
}
